import { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { NAV_AND_SUBNAV_HEIGHT } from '~/common/constants/page';
import { PersistedTable } from '~/components/Shared/PersistedTable/PersistedTable';
import { usePanelContext } from '~/eds/src/components/Panel/PanelContext';
import { TableContextType } from '~/enums/TableContextType';
import { GroupNode } from '~/redux/api/methods/group-nodes/types';
import { SearchFilter } from '~/types/DashboardV2';

import { convertSearchFieldToColumn } from '../helpers/convertSearchFieldToColumn';
import { EnrichedGroupNode } from '../helpers/NodeEnricher/NodeEnricher';
import { TreeBrowserNode } from '../helpers/TreeBrowser/types';
import { EsignatureBundleModal } from './EsignatureBundleModal';
import { useEsignatureBundleAction } from './hooks/useEsignatureBundleAction';
import { LoadMoreCell } from './LoadMoreCell';
import { MoveToParentPanel, MoveToParentPanelId } from './MoveToParentPanel';
import { DropInfo, NameCell } from './NameCell';

const messages = defineMessages({
  nameColumnTitle: {
    id: 'WorkflowGroupNodesTable.nameColumnTitle',
    defaultMessage: 'Name',
    description: 'Column title for node name',
  },
  typeColumnTitle: {
    id: 'WorkflowGroupNodesTable.typeColumnTitle',
    defaultMessage: 'Item Type',
    description: 'Column title for resource type',
  },
  unlink: {
    id: 'WorkflowGroupNodesTable.unlink',
    defaultMessage: 'Unlink',
    description: 'Action to move item to root level',
  },
  removeFromGroup: {
    id: 'WorkflowGroupNodesTable.removeFromGroup',
    defaultMessage: 'Remove from group',
    description: 'Action to remove item from group',
  },
  deleteSelected: {
    id: 'WorkflowGroupNodesTable.deleteSelected',
    defaultMessage: 'Delete',
    description: 'Button to delete selected items',
  },
  moveSelected: {
    id: 'WorkflowGroupNodesTable.moveSelected',
    defaultMessage: 'Move',
    description: 'Button to move selected items to a new parent',
  },
  unlinkSelected: {
    id: 'WorkflowGroupNodesTable.unlinkSelected',
    defaultMessage: 'Unlink',
    description: 'Button to unlink selected items to root level',
  },
});

export interface WorkflowGroupNodesTableProps {
  documentDynamicFields?: Record<string, SearchFilter>;
  hasEditPermission?: boolean;
  isLoading: boolean;
  nodes: TreeBrowserNode<EnrichedGroupNode>[];
  rootNodeId?: string;
  ticketDynamicFields?: Record<string, SearchFilter>;
  totalCount?: number;
  getDescendants: (nodeIds: string[]) => string[];
  isValidDropTarget?: (draggedNodeId: string, targetNodeId: string) => boolean;
  onCollapse: (node: TreeBrowserNode<EnrichedGroupNode>) => void;
  onDelete?: (nodeIds: string[]) => void;
  onExpand: (node: TreeBrowserNode<EnrichedGroupNode>) => void;
  onLoadAllNodes: () => Promise<TreeBrowserNode<EnrichedGroupNode>[]>;
  onLoadMore?: (nodeId: string) => Promise<void>;
  onMove?: (nodeIds: string[], newParentNodeId: string) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onRowClick?: (node: TreeBrowserNode<EnrichedGroupNode>) => void;
  onUnlink?: (nodeIds: string[], isAllSelected?: boolean) => void;
}

export const WorkflowGroupNodesTable = ({
  documentDynamicFields,
  hasEditPermission,
  isLoading,
  nodes,
  rootNodeId,
  ticketDynamicFields,
  totalCount,
  isValidDropTarget,
  onCollapse,
  onDelete,
  onExpand,
  onMove,
  onRowClick,
  onUnlink,
  onLoadMore,
  onLoadAllNodes,
  getDescendants,
  onPageSizeChange,
}: WorkflowGroupNodesTableProps) => {
  const intl = useIntl();
  const { openPanel } = usePanelContext();
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  const { getEsignatureBundleAction, modalState, closeModal } =
    useEsignatureBundleAction(nodes);

  const handleTableUpdate = useCallback(
    (state: { selectedRowIds?: string[] }) => {
      if (state.selectedRowIds) {
        setSelectedRowIds(state.selectedRowIds);
      }
    },
    [],
  );

  const handleRowClick = useCallback(
    (node: TreeBrowserNode<EnrichedGroupNode>) => {
      onRowClick?.(node);
    },
    [onRowClick],
  );

  const handleSingleMove = useCallback(
    ({ nodeId, newParentNodeId }: DropInfo) => {
      onMove?.([nodeId], newParentNodeId);
    },
    [onMove],
  );

  const handleBulkMove = useCallback(
    (nodeIds: string[], newParentNodeId: string) => {
      onMove?.(nodeIds, newParentNodeId);
    },
    [onMove],
  );

  // Create columns for dynamic fields
  const dynamicFieldColumns = useMemo(() => {
    // Merge document and ticket fields, avoiding duplicates by field ID
    const allFields: Record<string, SearchFilter> = {
      ...documentDynamicFields,
      ...ticketDynamicFields,
    };

    return Object.values(allFields)
      .filter((field) => field.id !== 'document_group_Document')
      .map(convertSearchFieldToColumn);
  }, [documentDynamicFields, ticketDynamicFields]);

  const columns = useMemo(
    () => [
      {
        key: 'name',
        title: intl.formatMessage(messages.nameColumnTitle),
        rawMinWidth: 140,
        cellType: 'custom',
        cellLayoutProps: { ml: 10 },
        disableSortBy: true,
        renderCell: (node: TreeBrowserNode<EnrichedGroupNode>) => {
          if (node.meta.isLoadMoreNode) {
            return (
              <LoadMoreCell
                indentLevel={node.meta.depth}
                onClick={async () => {
                  await onLoadMore?.(node.attributes.parentNodeId ?? '');
                }}
              />
            );
          }

          return (
            <NameCell
              node={node}
              name={node?.enrichedFields?.name}
              indentLevel={node.meta.depth}
              isExpanded={!!node.meta.isExpanded}
              isValidDropTarget={
                hasEditPermission ? isValidDropTarget : undefined
              }
              isPermissionDenied={node.isPermissionDenied ?? false}
              onCollapse={onCollapse}
              onExpand={onExpand}
              onHeldOver={onExpand}
              isLoading={!!(node.meta.isLoading || node.meta.isDeleting)}
              onDrop={hasEditPermission ? handleSingleMove : undefined}
            />
          );
        },
      },
      {
        key: 'type',
        title: intl.formatMessage(messages.typeColumnTitle),
        cellType: 'text',
        disableSortBy: true,
        mapCellProps: (node: TreeBrowserNode<GroupNode>) => ({
          text: node.meta.isLoadMoreNode
            ? ' '
            : node.attributes.resourceType || ' ',
        }),
      },
      ...dynamicFieldColumns,
    ],
    [intl, dynamicFieldColumns, hasEditPermission, handleSingleMove],
  );

  const rowActions = useMemo(
    () =>
      hasEditPermission
        ? [
            {
              label: intl.formatMessage(messages.unlink),
              onClick: (node: TreeBrowserNode<EnrichedGroupNode>) => {
                onUnlink?.([node.id as string]);
              },
              condition: (node: TreeBrowserNode<EnrichedGroupNode>) =>
                // Guard against loading placeholder rows (just {id: number})
                'meta' in node &&
                !node.meta.isLoadMoreNode &&
                node.attributes.parentNodeId !== rootNodeId,
            },
            {
              label: intl.formatMessage(messages.removeFromGroup),
              onClick: (node: TreeBrowserNode<EnrichedGroupNode>) => {
                onDelete?.([node.id as string]);
              },
              condition: (node: TreeBrowserNode<EnrichedGroupNode>) =>
                'meta' in node && !node.meta.isLoadMoreNode,
            },
          ]
        : [],
    [intl, rootNodeId, onDelete, onUnlink, hasEditPermission],
  );

  const getBulkActions = useCallback(
    ({ selectedRowIds: ids }: { selectedRowIds: string[] }) => {
      const actions = hasEditPermission
        ? [
            {
              icon: 'move',
              label: intl.formatMessage(messages.moveSelected),
              onClick: () => {
                openPanel(MoveToParentPanelId);
              },
            },
            {
              icon: 'unlink',
              label: intl.formatMessage(messages.unlinkSelected),
              onClick: ({
                selectedRowIds,
                isAllRowsSelected,
              }: {
                selectedRowIds: string[];
                isAllRowsSelected: boolean;
              }) => {
                onUnlink?.(selectedRowIds, isAllRowsSelected);
              },
            },
          ]
        : [];

      const esignatureAction = getEsignatureBundleAction(ids);
      if (esignatureAction) {
        actions.push(esignatureAction);
      }

      return actions;
    },
    [hasEditPermission, intl, openPanel, onUnlink, getEsignatureBundleAction],
  );

  const tableOptions = useMemo(
    () => ({
      enablePagination: false,
      enableManageColumns: true,
      enableSelectRows: hasEditPermission,
      enableStickyHeader: true,
      stickyHeaderOffset: NAV_AND_SUBNAV_HEIGHT,
    }),
    [hasEditPermission],
  );

  return (
    <>
      <PersistedTable
        context={TableContextType.Groups}
        name="workflow-group-nodes"
        isLoading={isLoading}
        columns={columns}
        data={nodes}
        totalCount={totalCount}
        mode={hasEditPermission ? 'select' : undefined}
        options={tableOptions}
        reactTableOptions={{
          manualSortBy: true,
        }}
        state={{ selectedRowIds }}
        onUpdate={handleTableUpdate}
        onPageSizeChange={onPageSizeChange}
        rowActions={rowActions}
        getBulkActions={getBulkActions}
        rowDetails={{
          onClick: handleRowClick,
          condition: (node: TreeBrowserNode<EnrichedGroupNode>) =>
            'meta' in node &&
            !node.meta.isLoadMoreNode &&
            !(node.isPermissionDenied ?? false),
        }}
      />

      <MoveToParentPanel
        selectedIds={selectedRowIds}
        onMove={handleBulkMove}
        onLoadAllNodes={onLoadAllNodes}
        getDescendants={getDescendants}
      />

      {modalState.providerType && (
        <EsignatureBundleModal
          isVisible={modalState.isOpen}
          providerType={modalState.providerType}
          tickets={modalState.tickets}
          onClose={closeModal}
        />
      )}
    </>
  );
};
